import { Injectable } from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';

import {
  DeltaService,
  Device,
  DevicesService,
  IEntityCache,
  IEntityCacheFinder,
  MainEntitiesProvider,
  TestListEntity,
  TestListService,
  TimeMode,
  TimestampRange,
  UserService,
  UnmanagedDevicesService,
  TwampManagedDevicesService,
  Application,
  ApplicationsService
} from '@nrwl/common-lib';
import { ControllerApiService } from '@nrwl/test-setup';

@Injectable({
  providedIn: 'root'
})
export class MainEntityService extends MainEntitiesProvider implements IEntityCacheFinder {
  firstRequest: boolean;
  entityCaches = {};
  isPmTestEnabled = true;

  constructor(
    private readonly userService: UserService,
    private readonly deltaService: DeltaService,
    private readonly devicesService: DevicesService,
    private readonly testListService: TestListService,
    private unmanagedDevicesService: UnmanagedDevicesService,
    private twampManagedDevicesService: TwampManagedDevicesService,
    private readonly applicationsService: ApplicationsService,
    private readonly controllerApiService: ControllerApiService
  ) {
    super();
    this.firstRequest = true;
    this.entityCaches[devicesService.getEntityName()] = devicesService;
    this.entityCaches[applicationsService.getEntityName()] = applicationsService;
    this.entityCaches[testListService.getEntityName()] = testListService;
    this.entityCaches[unmanagedDevicesService.getEntityName()] = unmanagedDevicesService;
    this.entityCaches[twampManagedDevicesService.getEntityName()] = twampManagedDevicesService;
    this.deltaService.deltaTime.subscribe((timeObj: TimestampRange) => {
      if (timeObj.toTime && timeObj.fromTime) {
        this.reloadEntities();
      }
    });
  }
  clearEntities(): void {
    this.deltaService.stop();
    this.devicesService.dispose();
    this.devicesService.clearRealtimeCache();
    this.testListService.dispose();
    this.testListService.clearRealtimeCache();
    this.userService.dispose();
    this.unmanagedDevicesService.dispose();
    this.twampManagedDevicesService.dispose();
    this.applicationsService.clearRealtimeCache();
    this.applicationsService.dispose();
  }

  findEntityCache(entityName: string): IEntityCache {
    return this.entityCaches[entityName];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async reloadEntities(): Promise<any> {
    // props not in use, just to be there
    const timeObj = {
      fromTime: Date.now() - 60 * 1000,
      toTime: Date.now()
    };
    const source = this.queryData(
      this.devicesService.realtimemonitorcache,
      this.testListService.realtimetestcache,
      timeObj,
      'real-time',
      this.unmanagedDevicesService.realtimemonitorcache,
      this.twampManagedDevicesService.realtimemonitorcache,
      this.applicationsService.realtimemonitorcache
    );
    return source.toPromise();
  }
  private queryData(
    devicesCache: Array<Device>,
    testListCache: Array<TestListEntity>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timeObj: TimestampRange,
    mode: TimeMode,
    // eslint-disable-next-line
    unmanagedDevicesCache: Array<any>,
    twampManagedDevicesCache: Array<any>,
    applicationscache: Array<Application>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    const devices = this.devicesService.updateDevices(
      devicesCache,
      timeObj,
      this.firstRequest,
      mode
    );
    this.isPmTestEnabled =
      this.controllerApiService.serverConfig?.['fusion-activepmtest']?.['enabled'] || true;
    const apps = this.isPmTestEnabled
      ? this.applicationsService.updateApplication(
          applicationscache,
          timeObj,
          this.firstRequest,
          mode
        )
      : new Subject<Array<Application>>();

    const testList = this.testListService.updateTestLists(
      testListCache,
      timeObj,
      this.firstRequest,
      mode
    );

    const managedTwampDevices =
      this.twampManagedDevicesService.managedDevices(twampManagedDevicesCache);
    const unmanagedDevices =
      this.unmanagedDevicesService.updateUnmanagedDevices(unmanagedDevicesCache);
    this.userService.updateUser();

    return forkJoin({
      devices: devices,
      apps: apps,
      testList: testList,
      unmanagedDevices: unmanagedDevices,
      twampManagedDevice: managedTwampDevices
    });
  }
}
